import type { QueryKey } from '@tanstack/react-query';

import nuc from '../../../../shared/utils/nuc';
import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import type { GetDependencyConfidenceResponse } from '../../../../shared/types/routes/binary';

const getReleaseDependencyConfidenceQueryKey = (
  releaseId: number,
  dependencyLocator: string,
): QueryKey => ['binary', 'release', releaseId, 'dependency-confidence', dependencyLocator];

function getReleaseDependencyConfidence(releaseId: number, dependencyLocator: string) {
  const baseUrl = `/api/binary/release/${releaseId}/dependency-confidence/${nuc(dependencyLocator)}`;
  return get<GetDependencyConfidenceResponse>(baseUrl);
}

/**
 * Helper hook for fetching the confidence of the dependencies of a user's project group release.
 * @param releaseId - The revision locator of the user's revision.
 * @param dependencyLocator - Dependency locator to filter results for a specific dependency.
 * @param shouldFetch - default true, allows hook to be skipped when project not available.
 * @returns A query for the confidence of the revision's dependencies.
 */
export default function useGetReleaseDependencyConfidence(
  releaseId: number,
  dependencyLocator: string,
  shouldFetch = true,
) {
  return useQuery<GetDependencyConfidenceResponse>({
    queryFn: () => getReleaseDependencyConfidence(releaseId, dependencyLocator),
    queryKey: getReleaseDependencyConfidenceQueryKey(releaseId, dependencyLocator),
    enabled: shouldFetch,
  });
}
